import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, FileText } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { bookingService } from '@/lib/api/booking.service';
import { quoteService } from '@/lib/api/quote.service';
import { serviceRequestApiService } from '@/lib/api/serviceRequest.service';
import { handleApiError } from '@/lib/api/client';
import type { Booking, BookingLineItem, BookingStatus } from '@/types/booking.types';

const BOOKING_STATUS_BADGE: Record<BookingStatus, { label: string; className: string }> = {
  pending:     { label: 'Pending',     className: 'bg-amber-100 text-amber-800' },
  confirmed:   { label: 'Confirmed',   className: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', className: 'bg-indigo-100 text-indigo-800' },
  completed:   { label: 'Completed',   className: 'bg-green-100 text-green-800' },
  cancelled:   { label: 'Cancelled',   className: 'bg-red-100 text-red-800' },
  disputed:    { label: 'Disputed',    className: 'bg-orange-100 text-orange-800' },
};

const formatCurrency = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const formatDate = (dateStr: string | null | undefined) =>
  dateStr ? new Date(dateStr).toLocaleDateString() : '—';

interface BookingDocument {
  name: string;
  url: string;
  uploaded_at: string;
}

export const BookingDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { celebrationId, bookingId } = useParams<{ celebrationId: string; bookingId: string }>();
  const { t } = useTranslation(['bookings', 'common']);

  const celebrationIdNum = useMemo(() => Number.parseInt(celebrationId ?? '', 10), [celebrationId]);
  const bookingIdNum = useMemo(() => Number.parseInt(bookingId ?? '', 10), [bookingId]);

  const [booking, setBooking] = useState<Booking | null>(null);
  const [lineItems, setLineItems] = useState<BookingLineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [finalizationPath, setFinalizationPath] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(celebrationIdNum) || !Number.isFinite(bookingIdNum)) return;
    setLoading(true);
    setError(null);
    try {
      const [bk, items] = await Promise.all([
        bookingService.getBooking(celebrationIdNum, bookingIdNum),
        bookingService.listLineItems(celebrationIdNum, bookingIdNum),
      ]);
      setBooking(bk);
      setLineItems(items);
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [celebrationIdNum, bookingIdNum]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const resolveFinalizationPath = async () => {
      if (!booking?.quote_id || !Number.isFinite(celebrationIdNum)) {
        setFinalizationPath(null);
        return;
      }

      try {
        const quote = await quoteService.getQuote(celebrationIdNum, booking.quote_id);
        const request = await serviceRequestApiService.getRequest(celebrationIdNum, quote.request_id);
        setFinalizationPath(
          `/celebrations/${celebrationIdNum}/services/${request.service_id}/requests/${quote.request_id}/vendors/${quote.vendor_id}/finalize`,
        );
      } catch {
        setFinalizationPath(null);
      }
    };

    void resolveFinalizationPath();
  }, [booking?.quote_id, celebrationIdNum]);

  const handleSignContract = async () => {
    if (!booking) return;
    setSigning(true);
    setSignError(null);
    try {
      await bookingService.signBooking(celebrationIdNum, bookingIdNum);
      await load();
    } catch (err: unknown) {
      setSignError(handleApiError(err));
    } finally {
      setSigning(false);
    }
  };

  const lineItemsTotal = useMemo(
    () => lineItems.reduce((sum, item) => sum + item.line_amount, 0),
    [lineItems],
  );

  const documents: BookingDocument[] = useMemo(() => {
    const docs = (booking?.metadata as Record<string, unknown> | undefined)?.documents;
    return Array.isArray(docs) ? (docs as BookingDocument[]) : [];
  }, [booking]);

  const statusBadge = booking ? BOOKING_STATUS_BADGE[booking.status] : null;
  const canSign = booking && !booking.contract_signed && booking.status !== 'completed' && booking.status !== 'cancelled';

  if (loading) {
    return (
      <PageContainer>
        <div className="py-12 text-center text-gray-500">{t('bookings:detail.loading')}</div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="py-12 text-center text-red-600">{error}</div>
      </PageContainer>
    );
  }

  if (!booking) {
    return (
      <PageContainer>
        <div className="py-12 text-center text-gray-500">{t('bookings:detail.notFound')}</div>
      </PageContainer>
    );
  }

  const bookingTitle = booking.booking_number
    ? `Booking #${booking.booking_number}`
    : `Booking #${booking.id}`;

  return (
    <PageContainer>
      <AppBreadcrumbs
        items={[
          { label: 'Bookings', to: `/celebrations/${celebrationId}/bookings` },
          { label: bookingTitle },
        ]}
      />

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{bookingTitle}</h1>
            {statusBadge && (
              <span className={`rounded-full px-3 py-1 text-sm font-medium ${statusBadge.className}`}>
                {statusBadge.label}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600">{t('bookings:detail.vendorLabel', { id: booking.vendor_id })}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ChevronLeft className="mr-1 h-4 w-4" />
          {t('common:actions.back')}
        </Button>
      </div>

      {finalizationPath && (
        <div className="mb-4">
          <Button variant="outline" size="sm" onClick={() => navigate(finalizationPath)}>
            {t('bookings:detail.backToFinalization')}
          </Button>
        </div>
      )}

      <div className="space-y-6">
        {/* Booking Details */}
        <Card>
          <CardHeader>
            <CardTitle>{t('bookings:details')}</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('bookings:detail.fields.agreedAmount')}</dt>
                <dd className="mt-1 text-base font-semibold text-gray-900">
                  {formatCurrency(booking.total_amount, booking.currency ?? 'USD')}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('bookings:detail.fields.paymentStatus')}</dt>
                <dd className="mt-1 text-base font-semibold capitalize text-gray-900">
                  {booking.payment_status ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('bookings:detail.fields.amountPaid')}</dt>
                <dd className="mt-1 text-base font-semibold text-gray-900">
                  {booking.paid_amount != null
                    ? formatCurrency(booking.paid_amount, booking.currency ?? 'USD')
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('bookings:detail.fields.contract')}</dt>
                <dd className="mt-1 text-base font-semibold text-gray-900">
                  {booking.contract_signed ? (
                    <span className="text-green-700">
                      {booking.contract_signed_at
                        ? t('bookings:detail.contract.signedOn', { date: formatDate(booking.contract_signed_at) })
                        : t('bookings:detail.contract.signedNoDate')}
                    </span>
                  ) : (
                    <span className="text-amber-700">{t('bookings:detail.contract.notSigned')}</span>
                  )}
                </dd>
              </div>
            </dl>

            {canSign && (
              <div className="mt-4">
                {signError && <p className="mb-2 text-sm text-red-600">{signError}</p>}
                <Button onClick={() => void handleSignContract()} disabled={signing} variant="outline">
                  {signing ? t('bookings:detail.contract.signing') : t('bookings:detail.contract.signButton')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader>
            <CardTitle>{t('bookings:detail.sections.lineItems')}</CardTitle>
          </CardHeader>
          <CardContent>
            {lineItems.length === 0 ? (
              <p className="text-sm text-gray-500">{t('bookings:detail.lineItems.empty')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      <th className="pb-2 pr-4">{t('bookings:detail.lineItems.columns.num')}</th>
                      <th className="pb-2 pr-4">{t('bookings:detail.lineItems.columns.item')}</th>
                      <th className="pb-2 pr-4">{t('bookings:detail.lineItems.columns.scheduled')}</th>
                      <th className="pb-2 pr-4">{t('bookings:detail.lineItems.columns.status')}</th>
                      <th className="pb-2 text-right">{t('bookings:detail.lineItems.columns.amount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {lineItems.map((item, idx) => (
                      <tr key={item.id} className="py-2">
                        <td className="py-2 pr-4 text-gray-500">{idx + 1}</td>
                        <td className="py-2 pr-4 font-medium text-gray-900">{item.description}</td>
                        <td className="py-2 pr-4 text-gray-600">{formatDate(item.scheduled_date)}</td>
                        <td className="py-2 pr-4 capitalize text-gray-600">{item.status}</td>
                        <td className="py-2 text-right text-gray-900">
                          {formatCurrency(item.line_amount, booking.currency ?? 'USD')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-semibold">
                      <td colSpan={4} className="pt-3 text-gray-700">{t('bookings:detail.lineItems.total')}</td>
                      <td className="pt-3 text-right text-gray-900">
                        {formatCurrency(lineItemsTotal, booking.currency ?? 'USD')}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/celebrations/${celebrationId}/bookings/${bookingId}/line-items`)}
              >
                {t('bookings:detail.lineItems.manage')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('bookings:detail.sections.documents')}</CardTitle>
              <Button variant="outline" size="sm" disabled>
                {t('bookings:detail.documents.upload')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <p className="text-sm text-gray-500">{t('bookings:detail.documents.empty')}</p>
            ) : (
              <ul className="divide-y">
                {documents.map((doc, idx) => (
                  <li key={idx} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">{t('bookings:detail.documents.uploaded', { date: formatDate(doc.uploaded_at) })}</p>
                      </div>
                    </div>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-blue-600 hover:underline"
                    >
                      {t('bookings:detail.documents.download')}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
};
